import { Lens } from "@hookform/lenses";
import { Box, ListItemText } from "@mui/material";
import { ReactElement, useCallback } from "react";

import {
  GetNgpVanSurveyQuestionQuery,
  GetNgpVanSurveyQuestionQueryVariables,
  GetNgpVanSurveyQuestionsQuery,
  GetNgpVanSurveyQuestionsQueryVariables,
  NgpVanSurveyQuestionFieldsFragment,
  NgpVanDatabaseModeGqlType,
  NgpVanObjectType,
} from "~frontend/__generated__/graphql";
import {
  GET_NGPVAN_SURVEY_QUESTION,
  GET_NGPVAN_SURVEY_QUESTIONS,
} from "~frontend/graphql/integration";
import { palette } from "~frontend/theme";

import { useWritableLens } from "../react-hook-form-components/lenses";
import NgpVanObjectAutocomplete from "./NgpVanObjectAutocomplete";
import { BaseProps, NgpVanActionFieldValues } from "./types";

const AUTO_REFRESH_SURVEY_QUESTIONS = 1000 * 60 * 60 * 24; // 1 day

export function NgpVanSurveyQuestionAutocomplete({
  onChange,
  ...baseProps
}: BaseProps & {
  onChange: (newValue: number | undefined) => void;
}): ReactElement {
  const { integrationId, databaseMode } = baseProps;

  return NgpVanObjectAutocomplete<
    NgpVanSurveyQuestionFieldsFragment,
    GetNgpVanSurveyQuestionQuery,
    GetNgpVanSurveyQuestionQueryVariables,
    GetNgpVanSurveyQuestionsQuery,
    GetNgpVanSurveyQuestionsQueryVariables
  >({
    ...baseProps,
    objectType: NgpVanObjectType.SurveyQuestions,
    autoRefreshWindowMillis: AUTO_REFRESH_SURVEY_QUESTIONS,
    noResultsLabel: "No Survey Questions Found",
    errorLabel: "Error Loading Survey Questions",
    onChange: newSurveyQuestion => {
      onChange(newSurveyQuestion?.surveyQuestionId ?? undefined);
    },
    getLastRefreshTime: useCallback(
      integration => {
        const maybeTimestamp =
          databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSurveyQuestionLastRefresh
            : integration.ngpVanMyCampaignSurveyQuestionLastRefresh;

        return maybeTimestamp ? new Date(maybeTimestamp) : null;
      },
      [databaseMode]
    ),
    getLastRefreshJob: useCallback(
      integration => {
        return (
          (databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSurveyQuestionRefreshJob
            : integration.ngpVanMyCampaignSurveyQuestionRefreshJob) ?? null
        );
      },
      [databaseMode]
    ),

    getObjectQuery: GET_NGPVAN_SURVEY_QUESTION,
    getObjectQueryVariables: useCallback(
      value => ({
        input: {
          integrationId,
          databaseMode,
          surveyQuestionId: value,
        },
      }),
      [integrationId, databaseMode]
    ),
    getObjectQueryResultToObject: useCallback(
      data => data.getNgpVanSurveyQuestion ?? null,
      []
    ),
    objectToGetObjectQueryResult: useCallback(
      surveyQuestion => ({
        getNgpVanSurveyQuestion: surveyQuestion,
      }),
      []
    ),

    listObjectQuery: GET_NGPVAN_SURVEY_QUESTIONS,
    listObjectQueryVariables: useCallback(
      nameContains => ({
        input: {
          integrationId,
          databaseMode,
          nameContains: nameContains || null,
        },
      }),
      [integrationId, databaseMode]
    ),
    listObjectQueryResultToObjects: useCallback(
      data => data.getNgpVanSurveyQuestions,
      []
    ),

    searchTextFromObject: useCallback(
      surveyQuestion => surveyQuestion.name,
      []
    ),
    valueFromObject: useCallback(
      surveyQuestion => surveyQuestion.surveyQuestionId,
      []
    ),
    listItemFromObject: useCallback(
      surveyQuestion => (
        <ListItemText
          primary={surveyQuestion.name}
          primaryTypographyProps={{ component: "div" }}
          secondary={
            surveyQuestion.scriptQuestion ? (
              <Box sx={{ fontSize: "12px", color: palette.navy }}>
                <i>{surveyQuestion.scriptQuestion}</i>
              </Box>
            ) : undefined
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      ),
      []
    ),
  });
}

export function RhfNgpVanSurveyQuestionAutocomplete({
  label,
  integrationId,
  databaseMode,
  readOnly,
  lens,
}: {
  label: string;
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  readOnly?: boolean;
  lens: Lens<NgpVanActionFieldValues>;
}): ReactElement {
  const { value, setValue, controller } = useWritableLens(lens);

  return (
    <NgpVanSurveyQuestionAutocomplete
      label={label}
      integrationId={integrationId}
      databaseMode={databaseMode}
      readOnly={readOnly}
      value={value.surveyQuestionId ? Number(value.surveyQuestionId) : null}
      onChange={async newValue => {
        setValue({
          ...value,
          surveyQuestionId: newValue ? String(newValue) : "",
          surveyResponseId: "",
        });
      }}
      error={Boolean(controller.fieldState.error)}
      helperText={controller.fieldState.error?.message}
    />
  );
}
